import React, { useContext, useState } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Toggle,
} from "carbon-components-react";
import { CheckmarkFilled16, Purchase32, Edit32 } from "@carbon/icons-react";

import AddOrEditActualEscrowModal from "../AddOrEditActualEscrowModal";
import AddOrEditPaymentModal from "../AddOrEditPaymentModal";
import ListPaymentsForPeriodModal from "../ListPaymentsForPeriodModal";
import { AmortizationContext } from "../AmortizationProvider";
import { dateToShortForm, dollarsTo2Places } from "../helpers";

const styles = require("./styles.module.scss");

const NUM_ROWS_TO_SHOW = 18;

// TODO: jjw: put field keys in shared/types/api/amortization.ts for consts here
const headers = [
  "Row",
  "Period",
  "Upload payments",

  // loan details
  "Starting principal",
  "Expected payment",
  "Expected interest payment",
  "Actual payment",
  "Payment delta",
  // "Additional payment", // NOTE: jjw: not yet implemented
  "Interest payment",
  "Principal payment",
  "Remaining principal",

  // escrow details
  "Escrow account balance",
  "Escrow payment",
  "Edit actual escrow payment",
  "Escrow tax payment",
  "Escrow insurance payment",
  "Escrow mortgage insurance payment",
  "Escrow disbursement",
].filter(Boolean);

const AmortizationTable = () => {
  const {
    busy,
    amortization: { loan_info, schedule },
    payments,
    actualEscrows,
  } = useContext(AmortizationContext);

  const [showingAllRows, setShowingAllRows] = useState<boolean>(false);
  const [showingPaymentsForPeriod, setShowingPaymentsForPeriod] = useState<string | null>(null);
  const [addingPaymentForPeriod, setAddingPaymentForPeriod] = useState<string | null>(null);
  const [editingPaymentId, setEditingPaymentId] = useState<number | null>(null);
  const [addingActualEscrowForPeriod, setAddingActualEscrowForPeriod] = useState<string | null>(
    null,
  );
  const [editingActualEscrowId, setEditingActualEscrowId] = useState<number | null>(null);

  const showAllRowsLabel = `Show all ${loan_info.term_in_months} rows`;
  const rowsToShow = showingAllRows ? schedule : schedule.slice(0, NUM_ROWS_TO_SHOW);

  return (
    <>
      {showingPaymentsForPeriod && !addingPaymentForPeriod && !editingPaymentId && (
        <ListPaymentsForPeriodModal
          period={showingPaymentsForPeriod}
          onAddPayment={period => setAddingPaymentForPeriod(period)}
          onEditPayment={paymentId => setEditingPaymentId(paymentId)}
          onClose={() => setShowingPaymentsForPeriod(null)}
        />
      )}
      {addingPaymentForPeriod && (
        <AddOrEditPaymentModal
          period={addingPaymentForPeriod}
          onClose={() => setAddingPaymentForPeriod(null)}
        />
      )}
      {editingPaymentId && (
        <AddOrEditPaymentModal
          paymentId={editingPaymentId}
          onClose={() => setEditingPaymentId(null)}
        />
      )}
      {addingActualEscrowForPeriod && (
        <AddOrEditActualEscrowModal
          period={addingActualEscrowForPeriod}
          onClose={() => setAddingActualEscrowForPeriod(null)}
        />
      )}
      {editingActualEscrowId && (
        <AddOrEditActualEscrowModal
          actualEscrowId={editingActualEscrowId}
          onClose={() => setEditingActualEscrowId(null)}
        />
      )}
      <div className={styles.container}>
        <div className={styles.toggle}>
          <Toggle
            labelA={showAllRowsLabel}
            labelB={showAllRowsLabel}
            size="sm"
            id="show-all-rows"
            disabled={busy}
            onToggle={() => setShowingAllRows(!showingAllRows)}
          />
        </div>
        <Table size="compact" useZebraStyles>
          <TableHead>
            <TableRow>
              {headers.map(h => (
                <TableHeader key={h}>{h}</TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsToShow.map(row => {
              const { month_number, month, loan_details, escrow_details } = row;

              const numPayments = payments.filter(p => p.payment_period === month).length;
              const actualEscrowId = actualEscrows.find(p => p.payment_period === month)?.id;

              return (
                <TableRow key={month}>
                  <TableCell>{month_number}</TableCell>
                  <TableCell>{dateToShortForm(month)}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      kind="ghost"
                      renderIcon={Purchase32}
                      iconDescription="upload payments"
                      disabled={busy}
                      onClick={() => {
                        if (numPayments === 0) {
                          setAddingPaymentForPeriod(month);
                          return;
                        }
                        setShowingPaymentsForPeriod(month);
                      }}
                    >
                      {numPayments === 0 ? "—" : `${numPayments}`}
                    </Button>
                  </TableCell>

                  {/* TODO: jjw: iterate over attrs in header */}
                  <TableCell>{dollarsTo2Places(loan_details.starting_principal)}</TableCell>
                  <TableCell>{dollarsTo2Places(loan_details.expected_payment)}</TableCell>
                  <TableCell>{dollarsTo2Places(loan_details.expected_interest_payment)}</TableCell>
                  <TableCell>{dollarsTo2Places(loan_details.actual_payment)}</TableCell>
                  <TableCell>{dollarsTo2Places(loan_details.payment_delta)}</TableCell>
                  {/* NOTE: jjw: not yet implemented */}
                  {/* <TableCell>{dollarsTo2Places(loan_details.additional_payment)}</TableCell> */}
                  <TableCell>{dollarsTo2Places(loan_details.interest_payment)}</TableCell>
                  <TableCell>{dollarsTo2Places(loan_details.principal_payment)}</TableCell>
                  <TableCell>{dollarsTo2Places(loan_details.remaining_principal)}</TableCell>
                  <TableCell>{dollarsTo2Places(escrow_details.escrow_account_balance)}</TableCell>
                  <TableCell>{dollarsTo2Places(escrow_details.escrow_payment)}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      kind="ghost"
                      renderIcon={Edit32}
                      iconDescription="edit actual escrow"
                      disabled={busy}
                      onClick={() => {
                        if (actualEscrowId) {
                          setEditingActualEscrowId(actualEscrowId);
                          return;
                        }
                        setAddingActualEscrowForPeriod(month);
                      }}
                    >
                      {!actualEscrowId ? "—" : <CheckmarkFilled16 />}
                    </Button>
                  </TableCell>
                  <TableCell>{dollarsTo2Places(escrow_details.tax_payment)}</TableCell>
                  <TableCell>{dollarsTo2Places(escrow_details.insurance_payment)}</TableCell>
                  <TableCell>
                    {dollarsTo2Places(escrow_details.mortgage_insurance_payment)}
                  </TableCell>
                  <TableCell>{dollarsTo2Places(escrow_details.escrow_disbursement)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
};

export default AmortizationTable;
